/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Bale
 * This is the API for Bale. At this time, it is not intended for use external to the official
 * Bale apps, and provides no stability or availability guarantees.
 *
 * In addition to everything documented explicitly in the OpenAPI specification, the following
 * considerations apply.
 *
 * ### URL conventions
 *
 * All paths in URLs __must not__ be passed with a trailing slash, if there is a trailing slash in the
 * path, the handler will not be found and the result will be `404 Not Found`.
 *
 * ### Error Responses
 *
 * For application level errors, the response is typically a JSON encoded [`ErrorDetailResponse`](#model/errordetailresponse),
 * and should be documented in the operation responses. For each operation, the list of possible values
 * for the `code` field is fixed, and the corresponding structure of the `details` field is dependent
 * on that code, but this level of detail is not documented at this time.
 *
 * ### Authentication Errors
 *
 * For any request that requires authentication, the `401 Unauthorized` response is also possible.
 * In this situation, the response body is a standard JSON encoded `ErrorDetailResponse`. No
 * `WWW-Authenticate` header is indicated, as we are not using any well known authorization scheme.
 *
 * ### JSON Requests
 *
 * In all operations where the API expects a JSON request body, the following apply:
 *
 * 1. The header `Content-Type: application/json` must be supplied, or the response status will be `415 Unsupported Media Type`
 * 2. The request body must be valid JSON, or the response status will be `400 Bad Request`
 * 3. If the body is valid JSON but does not match the expected type according to the schema, the response status will be `422 Unprocessable Content`
 *
 * These responses will have `Content-Type: text/plain; charset=utf-8` and include only a
 * developer facing message in the body. As these restrictions are applied at the framework
 * level, they do not follow the conventional error details format used for application
 * level errors.
 *
 * Further validation (e.g. value ranges, patterns, lengths, beyond what can be handled by the type system)
 * may be applied by the operation itself, in which case the `422 Unprocessable Content` response is
 * explicitly documented, and an error with the code `ValidationError` may be returned. In this case,
 * the detail field matches the schema [`ValidationError`](#model/validationerror) and can be used
 * to highlight specific issues for the user to address.
 *
 * OpenAPI spec version: 0.1.0
 */
import * as zod from "zod/mini";

export const AccountIdOrMe = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod.enum(["@me"]),
  /*#__PURE__*/ zod.string(),
]);

export type AccountIdOrMe = zod.input<typeof AccountIdOrMe>;
export type AccountIdOrMeOutput = zod.output<typeof AccountIdOrMe>;

export const AddFriendGroupMemberRequest = /*#__PURE__*/ zod.strictObject({
  friend_id: /*#__PURE__*/ zod.string(),
});

export type AddFriendGroupMemberRequest = zod.input<typeof AddFriendGroupMemberRequest>;
export type AddFriendGroupMemberRequestOutput = zod.output<typeof AddFriendGroupMemberRequest>;

export const FriendGroup = /*#__PURE__*/ zod.strictObject({
  created_at: /*#__PURE__*/ zod.date(),
  description: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  id: /*#__PURE__*/ zod.int(),
  name: /*#__PURE__*/ zod.string(),
  sort: /*#__PURE__*/ zod.int(),
});

export type FriendGroup = zod.input<typeof FriendGroup>;
export type FriendGroupOutput = zod.output<typeof FriendGroup>;

export const AddFriendGroupMemberResponse = /*#__PURE__*/ zod.strictObject({
  friend_group: FriendGroup,
  friend_id: /*#__PURE__*/ zod.string(),
});

export type AddFriendGroupMemberResponse = zod.input<typeof AddFriendGroupMemberResponse>;
export type AddFriendGroupMemberResponseOutput = zod.output<typeof AddFriendGroupMemberResponse>;

export const AvatarMimeType = /*#__PURE__*/ zod.enum([
  "image/png",
  "image/gif",
  "image/jpeg",
  "image/avif",
  "image/webp",
]);

export type AvatarMimeType = zod.input<typeof AvatarMimeType>;
export type AvatarMimeTypeOutput = zod.output<typeof AvatarMimeType>;

export const Calendar = /*#__PURE__*/ zod.strictObject({
  attending_url: /*#__PURE__*/ zod
    .string()
    .check(
      /*#__PURE__*/ zod.describe(
        "An iCalendar that includes only events the account is attending.",
      ),
    ),
  id: /*#__PURE__*/ zod.uuid(),
  name: /*#__PURE__*/ zod.string(),
  visible_url: /*#__PURE__*/ zod
    .string()
    .check(
      /*#__PURE__*/ zod.describe("An iCalendar that includes all events visible to the account."),
    ),
});

export type Calendar = zod.input<typeof Calendar>;
export type CalendarOutput = zod.output<typeof Calendar>;

export const createAccountAbuseReportRequestDescriptionMax = 65536;

export const CreateAccountAbuseReportRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createAccountAbuseReportRequestDescriptionMax)),
});

export type CreateAccountAbuseReportRequest = zod.input<typeof CreateAccountAbuseReportRequest>;
export type CreateAccountAbuseReportRequestOutput = zod.output<
  typeof CreateAccountAbuseReportRequest
>;

export const createAccountRequestOneIdRegExp = new RegExp("^[\\w\\d_-]{3,64}$");
export const createAccountRequestOnePasswordMin = 12;

export const CreateAccountRequest = /*#__PURE__*/ zod.strictObject({
  email: /*#__PURE__*/ zod.string(),
  id: /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.regex(createAccountRequestOneIdRegExp)),
  login_method: /*#__PURE__*/ zod.enum(["Password"]),
  password: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(createAccountRequestOnePasswordMin)),
});

export type CreateAccountRequest = zod.input<typeof CreateAccountRequest>;
export type CreateAccountRequestOutput = zod.output<typeof CreateAccountRequest>;

export const CreateAccountResponse = /*#__PURE__*/ zod.strictObject({
  id: /*#__PURE__*/ zod.string(),
});

export type CreateAccountResponse = zod.input<typeof CreateAccountResponse>;
export type CreateAccountResponseOutput = zod.output<typeof CreateAccountResponse>;

export const createCalendarRequestNameMax = 128;

export const CreateCalendarRequest = /*#__PURE__*/ zod.strictObject({
  name: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createCalendarRequestNameMax)),
});

export type CreateCalendarRequest = zod.input<typeof CreateCalendarRequest>;
export type CreateCalendarRequestOutput = zod.output<typeof CreateCalendarRequest>;

export const CreateCalendarResponse = /*#__PURE__*/ zod.strictObject({
  calendar: Calendar,
});

export type CreateCalendarResponse = zod.input<typeof CreateCalendarResponse>;
export type CreateCalendarResponseOutput = zod.output<typeof CreateCalendarResponse>;

export const CreateEmailAddressRequest = /*#__PURE__*/ zod.strictObject({
  email: /*#__PURE__*/ zod.string(),
});

export type CreateEmailAddressRequest = zod.input<typeof CreateEmailAddressRequest>;
export type CreateEmailAddressRequestOutput = zod.output<typeof CreateEmailAddressRequest>;

export const EmailAddress = /*#__PURE__*/ zod.strictObject({
  email_address: /*#__PURE__*/ zod.string(),
  id: /*#__PURE__*/ zod.int(),
  is_primary: /*#__PURE__*/ zod.boolean(),
  is_verified: /*#__PURE__*/ zod.boolean(),
});

export type EmailAddress = zod.input<typeof EmailAddress>;
export type EmailAddressOutput = zod.output<typeof EmailAddress>;

export const CreateEmailAddressResponse = /*#__PURE__*/ zod.strictObject({
  email_address: EmailAddress,
});

export type CreateEmailAddressResponse = zod.input<typeof CreateEmailAddressResponse>;
export type CreateEmailAddressResponseOutput = zod.output<typeof CreateEmailAddressResponse>;

export const createEventAbuseReportRequestDescriptionMax = 65536;

export const CreateEventAbuseReportRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createEventAbuseReportRequestDescriptionMax)),
});

export type CreateEventAbuseReportRequest = zod.input<typeof CreateEventAbuseReportRequest>;
export type CreateEventAbuseReportRequestOutput = zod.output<typeof CreateEventAbuseReportRequest>;

export const createEventCommentRequestContentMax = 65536;

export const CreateEventCommentRequest = /*#__PURE__*/ zod.strictObject({
  content: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createEventCommentRequestContentMax)),
  reply_to_comment_id: /*#__PURE__*/ zod.nullable(/*#__PURE__*/ zod.int()),
});

export type CreateEventCommentRequest = zod.input<typeof CreateEventCommentRequest>;
export type CreateEventCommentRequestOutput = zod.output<typeof CreateEventCommentRequest>;

export const TextContent = /*#__PURE__*/ zod
  .strictObject({
    html: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
    raw: /*#__PURE__*/ zod.string(),
  })
  .check(
    /*#__PURE__*/ zod.describe(
      "Large text content that supports Markdown syntax.\n\nValid Markdown can be rendered to HTML by the server.",
    ),
  );

export type TextContent = zod.input<typeof TextContent>;
export type TextContentOutput = zod.output<typeof TextContent>;

export const EventAction = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod.strictObject({
    action: /*#__PURE__*/ zod.enum(["Created"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    action: /*#__PURE__*/ zod.enum(["Updated"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    action: /*#__PURE__*/ zod.enum(["Joined"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    action: /*#__PURE__*/ zod.enum(["Left"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    action: /*#__PURE__*/ zod.enum(["Commented"]),
    content: TextContent,
    reply_to_comment_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.int()),
  }),
]);

export type EventAction = zod.input<typeof EventAction>;
export type EventActionOutput = zod.output<typeof EventAction>;

export const EventActionLogEntry = /*#__PURE__*/ zod.intersection(
  EventAction,
  /*#__PURE__*/ zod.strictObject({
    id: /*#__PURE__*/ zod.int(),
    occurred_at: /*#__PURE__*/ zod.date(),
  }),
);

export type EventActionLogEntry = zod.input<typeof EventActionLogEntry>;
export type EventActionLogEntryOutput = zod.output<typeof EventActionLogEntry>;

export const CreateEventCommentResponse = /*#__PURE__*/ zod.strictObject({
  event_comment: EventActionLogEntry.check(
    /*#__PURE__*/ zod.describe('This will be an event action log with action `"commented"`.'),
  ),
});

export type CreateEventCommentResponse = zod.input<typeof CreateEventCommentResponse>;
export type CreateEventCommentResponseOutput = zod.output<typeof CreateEventCommentResponse>;

export const createEventDirectionsRequestDirectionsMax = 65536;

export const createEventDirectionsRequestPlaceIdMax = 256;

export const CreateEventDirectionsRequest = /*#__PURE__*/ zod.strictObject({
  directions: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(createEventDirectionsRequestDirectionsMax)),
  place_id: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(createEventDirectionsRequestPlaceIdMax)),
});

export type CreateEventDirectionsRequest = zod.input<typeof CreateEventDirectionsRequest>;
export type CreateEventDirectionsRequestOutput = zod.output<typeof CreateEventDirectionsRequest>;

export const EventNote = /*#__PURE__*/ zod.strictObject({
  content: TextContent,
  title: /*#__PURE__*/ zod.string(),
});

export type EventNote = zod.input<typeof EventNote>;
export type EventNoteOutput = zod.output<typeof EventNote>;

export const EventDirections = /*#__PURE__*/ zod.strictObject({
  directions: TextContent,
  place_id: /*#__PURE__*/ zod.string(),
});

export type EventDirections = zod.input<typeof EventDirections>;
export type EventDirectionsOutput = zod.output<typeof EventDirections>;

export const EventPollOption = /*#__PURE__*/ zod.strictObject({
  account_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  id: /*#__PURE__*/ zod.int(),
  title: /*#__PURE__*/ zod.string(),
});

export type EventPollOption = zod.input<typeof EventPollOption>;
export type EventPollOptionOutput = zod.output<typeof EventPollOption>;

export const EventPollVote = /*#__PURE__*/ zod.strictObject({
  account_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  option_id: /*#__PURE__*/ zod.int(),
});

export type EventPollVote = zod.input<typeof EventPollVote>;
export type EventPollVoteOutput = zod.output<typeof EventPollVote>;

export const EventPoll = /*#__PURE__*/ zod.strictObject({
  allow_custom_options: /*#__PURE__*/ zod.boolean(),
  is_anonymous: /*#__PURE__*/ zod.boolean(),
  is_response_editable: /*#__PURE__*/ zod.boolean(),
  is_result_visible_before_voting: /*#__PURE__*/ zod.boolean(),
  max_selections: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.int()),
  options: /*#__PURE__*/ zod.array(EventPollOption),
  subject: TextContent,
  votes: /*#__PURE__*/ zod.array(EventPollVote),
});

export type EventPoll = zod.input<typeof EventPoll>;
export type EventPollOutput = zod.output<typeof EventPoll>;

export const EventWidgetData = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod.strictObject({
    widget_type: /*#__PURE__*/ zod.enum(["summary"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    widget_type: /*#__PURE__*/ zod.enum(["attending"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    widget_type: /*#__PURE__*/ zod.enum(["discussion"]),
  }),
  /*#__PURE__*/ zod.intersection(
    EventNote,
    /*#__PURE__*/ zod.strictObject({
      widget_type: /*#__PURE__*/ zod.enum(["note"]),
    }),
  ),
  /*#__PURE__*/ zod.intersection(
    EventDirections,
    /*#__PURE__*/ zod.strictObject({
      widget_type: /*#__PURE__*/ zod.enum(["directions"]),
    }),
  ),
  /*#__PURE__*/ zod.intersection(
    EventPoll,
    /*#__PURE__*/ zod.strictObject({
      widget_type: /*#__PURE__*/ zod.enum(["poll"]),
    }),
  ),
]);

export type EventWidgetData = zod.input<typeof EventWidgetData>;
export type EventWidgetDataOutput = zod.output<typeof EventWidgetData>;

export const EventWidget = /*#__PURE__*/ zod.intersection(
  EventWidgetData,
  /*#__PURE__*/ zod.strictObject({
    column: /*#__PURE__*/ zod.int(),
    id: /*#__PURE__*/ zod.uuid(),
    row: /*#__PURE__*/ zod.int(),
  }),
);

export type EventWidget = zod.input<typeof EventWidget>;
export type EventWidgetOutput = zod.output<typeof EventWidget>;

export const CreateEventDirectionsResponse = /*#__PURE__*/ zod.strictObject({
  event_directions: EventWidget,
});

export type CreateEventDirectionsResponse = zod.input<typeof CreateEventDirectionsResponse>;
export type CreateEventDirectionsResponseOutput = zod.output<typeof CreateEventDirectionsResponse>;

export const EventInviteCode = /*#__PURE__*/ zod.strictObject({
  account_id: /*#__PURE__*/ zod.string(),
  event_id: /*#__PURE__*/ zod.int(),
  id: /*#__PURE__*/ zod.string(),
  url: /*#__PURE__*/ zod.string(),
});

export type EventInviteCode = zod.input<typeof EventInviteCode>;
export type EventInviteCodeOutput = zod.output<typeof EventInviteCode>;

export const CreateEventInviteCodeResponse = /*#__PURE__*/ zod.strictObject({
  event_invite_code: EventInviteCode,
});

export type CreateEventInviteCodeResponse = zod.input<typeof CreateEventInviteCodeResponse>;
export type CreateEventInviteCodeResponseOutput = zod.output<typeof CreateEventInviteCodeResponse>;

export const createEventNoteRequestContentMax = 65536;

export const createEventNoteRequestTitleMax = 256;

export const CreateEventNoteRequest = /*#__PURE__*/ zod.strictObject({
  content: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(createEventNoteRequestContentMax))
    .check(/*#__PURE__*/ zod.describe("Markdown note content.")),
  title: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(createEventNoteRequestTitleMax)),
});

export type CreateEventNoteRequest = zod.input<typeof CreateEventNoteRequest>;
export type CreateEventNoteRequestOutput = zod.output<typeof CreateEventNoteRequest>;

export const CreateEventNoteResponse = /*#__PURE__*/ zod.strictObject({
  event_note: EventWidget,
});

export type CreateEventNoteResponse = zod.input<typeof CreateEventNoteResponse>;
export type CreateEventNoteResponseOutput = zod.output<typeof CreateEventNoteResponse>;

export const createEventPollOptionTitleMax = 500;

export const CreateEventPollOption = /*#__PURE__*/ zod.strictObject({
  title: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createEventPollOptionTitleMax)),
});

export type CreateEventPollOption = zod.input<typeof CreateEventPollOption>;
export type CreateEventPollOptionOutput = zod.output<typeof CreateEventPollOption>;

export const createEventPollRequestSubjectMax = 1024;

export const CreateEventPollRequest = /*#__PURE__*/ zod.strictObject({
  allow_custom_options: /*#__PURE__*/ zod.boolean(),
  is_anonymous: /*#__PURE__*/ zod.boolean(),
  is_response_editable: /*#__PURE__*/ zod.boolean(),
  is_result_visible_before_voting: /*#__PURE__*/ zod.boolean(),
  max_selections: /*#__PURE__*/ zod.nullish(
    /*#__PURE__*/ zod.int().check(/*#__PURE__*/ zod.gte(1)),
  ),
  options: /*#__PURE__*/ zod.array(CreateEventPollOption),
  subject: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createEventPollRequestSubjectMax)),
});

export type CreateEventPollRequest = zod.input<typeof CreateEventPollRequest>;
export type CreateEventPollRequestOutput = zod.output<typeof CreateEventPollRequest>;

export const CreateEventPollResponse = /*#__PURE__*/ zod.strictObject({
  event_poll: EventWidget,
});

export type CreateEventPollResponse = zod.input<typeof CreateEventPollResponse>;
export type CreateEventPollResponseOutput = zod.output<typeof CreateEventPollResponse>;

export const DegreeOfSeparation = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod.enum(["unlimited"]),
  /*#__PURE__*/ zod.int(),
]);

export type DegreeOfSeparation = zod.input<typeof DegreeOfSeparation>;
export type DegreeOfSeparationOutput = zod.output<typeof DegreeOfSeparation>;

export const createEventRequestDescriptionMax = 65536;

export const createEventRequestLocationMax = 512;

export const createEventRequestMaxAttendeesMin = 2;

export const createEventRequestNameMax = 256;

export const CreateEventRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod
    .nullable(
      /*#__PURE__*/ zod
        .string()
        .check(/*#__PURE__*/ zod.maxLength(createEventRequestDescriptionMax)),
    )
    .check(/*#__PURE__*/ zod.describe("Details shown to anyone who can see this event.")),
  discoverable_distance: /*#__PURE__*/ zod.exactOptional(
    /*#__PURE__*/ zod.union([
      /*#__PURE__*/ zod.null(),
      DegreeOfSeparation.check(
        /*#__PURE__*/ zod.describe(
          "Without receiving an explicit event invite or share to an event, an event is discoverable to\nfriends of attendees up to a maximum distance from the host. Set to 0, only friends who have\nreceived an invite or share can see this event. The special value `unlimited` indicates the\nevent may be discoverable at any distance from the host.",
        ),
      ),
    ]),
  ),
  end_time: /*#__PURE__*/ zod.nullable(/*#__PURE__*/ zod.date()),
  location: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.maxLength(createEventRequestLocationMax)),
  ),
  max_attendees: /*#__PURE__*/ zod
    .nullable(
      /*#__PURE__*/ zod.int().check(/*#__PURE__*/ zod.gte(createEventRequestMaxAttendeesMin)),
    )
    .check(
      /*#__PURE__*/ zod.describe(
        "Set a limit on how many people may join this event. Leave null for unlimited.",
      ),
    ),
  name: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod
      .string()
      .check(/*#__PURE__*/ zod.minLength(1))
      .check(/*#__PURE__*/ zod.maxLength(createEventRequestNameMax)),
  ),
  start_time: /*#__PURE__*/ zod.nullable(/*#__PURE__*/ zod.date()),
  template_id: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.int())
    .check(
      /*#__PURE__*/ zod.describe(
        "Clone this event from a specified event. Not all details can be cloned.\n\nNon-null fields in the body will be used in favour of values from the\nbase event.",
      ),
    ),
});

export type CreateEventRequest = zod.input<typeof CreateEventRequest>;
export type CreateEventRequestOutput = zod.output<typeof CreateEventRequest>;

export const Event = /*#__PURE__*/ zod.strictObject({
  account_id: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.describe("The account ID of the host of this event.")),
  description: TextContent.check(
    /*#__PURE__*/ zod.describe("A publicly visible long description of this event."),
  ),
  end_time: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.date())
    .check(
      /*#__PURE__*/ zod.describe(
        "The date and time at which this event takes place. If not set, and the start date is in the past, consider this an ongoing live event.",
      ),
    ),
  id: /*#__PURE__*/ zod.int(),
  location: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.string())
    .check(/*#__PURE__*/ zod.describe("The location of this event.")),
  max_attendees: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.int())
    .check(
      /*#__PURE__*/ zod.describe(
        "The maximum number of people who are able to join this event as an attendee. If null, there is no limit.",
      ),
    ),
  name: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.describe("The publicly visible name of this event.")),
  start_time: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.date())
    .check(
      /*#__PURE__*/ zod.describe(
        "The date and time at which this event takes place. If not set, the event's date is undecided.",
      ),
    ),
});

export type Event = zod.input<typeof Event>;
export type EventOutput = zod.output<typeof Event>;

export const UploadedImage = /*#__PURE__*/ zod.strictObject({
  id: /*#__PURE__*/ zod.int(),
  public_url: /*#__PURE__*/ zod.url(),
});

export type UploadedImage = zod.input<typeof UploadedImage>;
export type UploadedImageOutput = zod.output<typeof UploadedImage>;

export const turtleEyeColorMin = 0;
export const turtleEyeColorMax = 16777215;

export const turtleShellColorMin = 0;
export const turtleShellColorMax = 16777215;

export const turtleSkinColorMin = 0;
export const turtleSkinColorMax = 16777215;

export const Turtle = /*#__PURE__*/ zod.strictObject({
  back_hat_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  eye_color: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(turtleEyeColorMin))
    .check(/*#__PURE__*/ zod.lte(turtleEyeColorMax)),
  head_hat_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  pants_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  pet_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  shell_color: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(turtleShellColorMin))
    .check(/*#__PURE__*/ zod.lte(turtleShellColorMax)),
  shirt_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  shoes_id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  skin_color: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(turtleSkinColorMin))
    .check(/*#__PURE__*/ zod.lte(turtleSkinColorMax)),
});

export type Turtle = zod.input<typeof Turtle>;
export type TurtleOutput = zod.output<typeof Turtle>;

export const Profile = /*#__PURE__*/ zod.strictObject({
  avatar: /*#__PURE__*/ zod.exactOptional(
    /*#__PURE__*/ zod.union([/*#__PURE__*/ zod.null(), UploadedImage]),
  ),
  biography: TextContent,
  created_at: /*#__PURE__*/ zod.date(),
  display_name: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  id: /*#__PURE__*/ zod.string(),
  pronouns: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  real_name: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  turtle: Turtle,
});

export type Profile = zod.input<typeof Profile>;
export type ProfileOutput = zod.output<typeof Profile>;

export const EventResponse = /*#__PURE__*/ zod.enum(["Attending", "NotAttending", "Ignored"]);

export type EventResponse = zod.input<typeof EventResponse>;
export type EventResponseOutput = zod.output<typeof EventResponse>;

export const EventParticipant = /*#__PURE__*/ zod.strictObject({
  discoverable_distance: DegreeOfSeparation.check(
    /*#__PURE__*/ zod.describe(
      "The distance to which this participant has set their discoverability setting.",
    ),
  ),
  effective_discoverable_distance: DegreeOfSeparation.check(
    /*#__PURE__*/ zod.describe(
      "The actual discoverability of this participant, taking into account restrictions\nset by the host and more priviliged attendees.",
    ),
  ),
  limiting_discoverable_distance: DegreeOfSeparation.check(
    /*#__PURE__*/ zod.describe("The maximum allowed discoverability value for this participant."),
  ),
  profile: Profile,
  responded_at: /*#__PURE__*/ zod.date(),
  response: EventResponse,
});

export type EventParticipant = zod.input<typeof EventParticipant>;
export type EventParticipantOutput = zod.output<typeof EventParticipant>;

export const CreateEventResponse = /*#__PURE__*/ zod.strictObject({
  event: Event,
  event_action_log: /*#__PURE__*/ zod.array(EventActionLogEntry),
  event_participants: /*#__PURE__*/ zod.array(EventParticipant),
});

export type CreateEventResponse = zod.input<typeof CreateEventResponse>;
export type CreateEventResponseOutput = zod.output<typeof CreateEventResponse>;

export const createFriendGroupRequestDescriptionMax = 512;

export const createFriendGroupRequestNameMax = 64;

export const CreateFriendGroupRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod.nullish(
    /*#__PURE__*/ zod
      .string()
      .check(/*#__PURE__*/ zod.maxLength(createFriendGroupRequestDescriptionMax)),
  ),
  name: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(createFriendGroupRequestNameMax)),
});

export type CreateFriendGroupRequest = zod.input<typeof CreateFriendGroupRequest>;
export type CreateFriendGroupRequestOutput = zod.output<typeof CreateFriendGroupRequest>;

export const CreateFriendGroupResponse = /*#__PURE__*/ zod.strictObject({
  friend_group: FriendGroup,
});

export type CreateFriendGroupResponse = zod.input<typeof CreateFriendGroupResponse>;
export type CreateFriendGroupResponseOutput = zod.output<typeof CreateFriendGroupResponse>;

export const createFriendRequestRequestNoteMax = 512;

export const CreateFriendRequestRequest = /*#__PURE__*/ zod.strictObject({
  friend_id: /*#__PURE__*/ zod.string(),
  note: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod
      .string()
      .check(/*#__PURE__*/ zod.maxLength(createFriendRequestRequestNoteMax)),
  ),
});

export type CreateFriendRequestRequest = zod.input<typeof CreateFriendRequestRequest>;
export type CreateFriendRequestRequestOutput = zod.output<typeof CreateFriendRequestRequest>;

export const FriendshipStatus = /*#__PURE__*/ zod.enum(["Requested", "Friends"]);

export type FriendshipStatus = zod.input<typeof FriendshipStatus>;
export type FriendshipStatusOutput = zod.output<typeof FriendshipStatus>;

export const CreateFriendRequestResponse = /*#__PURE__*/ zod.strictObject({
  friend_id: /*#__PURE__*/ zod.string(),
  status: FriendshipStatus,
});

export type CreateFriendRequestResponse = zod.input<typeof CreateFriendRequestResponse>;
export type CreateFriendRequestResponseOutput = zod.output<typeof CreateFriendRequestResponse>;

export const CreatePasswordResetRequestRequest = /*#__PURE__*/ zod.strictObject({
  email: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.describe("An email address associated with the account.")),
});

export type CreatePasswordResetRequestRequest = zod.input<typeof CreatePasswordResetRequestRequest>;
export type CreatePasswordResetRequestRequestOutput = zod.output<
  typeof CreatePasswordResetRequestRequest
>;

export const DismissFriendRequestRequest = /*#__PURE__*/ zod.strictObject({
  friend_id: /*#__PURE__*/ zod.string(),
});

export type DismissFriendRequestRequest = zod.input<typeof DismissFriendRequestRequest>;
export type DismissFriendRequestRequestOutput = zod.output<typeof DismissFriendRequestRequest>;

export const EmailVerificationRequest = /*#__PURE__*/ zod.strictObject({
  email_address: /*#__PURE__*/ zod.string(),
  expires_at: /*#__PURE__*/ zod.date(),
  id: /*#__PURE__*/ zod.uuid(),
});

export type EmailVerificationRequest = zod.input<typeof EmailVerificationRequest>;
export type EmailVerificationRequestOutput = zod.output<typeof EmailVerificationRequest>;

export const ErrorDetailResponse = /*#__PURE__*/ zod.strictObject({
  code: /*#__PURE__*/ zod
    .string()
    .check(
      /*#__PURE__*/ zod.describe("An error code that uniquely identifies the error per operation."),
    ),
  detail: /*#__PURE__*/ zod
    .unknown()
    .check(
      /*#__PURE__*/ zod.describe(
        "Additional details which may be helpful in debugging, or when rendering a user facing error message.",
      ),
    ),
  message: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.describe("Developer facing message for debugging this error.")),
});

export type ErrorDetailResponse = zod.input<typeof ErrorDetailResponse>;
export type ErrorDetailResponseOutput = zod.output<typeof ErrorDetailResponse>;

export const EventInvite = /*#__PURE__*/ zod.strictObject({
  discoverable_distance: DegreeOfSeparation,
  invited: Profile,
  inviter_account_id: /*#__PURE__*/ zod.string(),
});

export type EventInvite = zod.input<typeof EventInvite>;
export type EventInviteOutput = zod.output<typeof EventInvite>;

export const EventParticipationLevel = /*#__PURE__*/ zod.enum(["Attending", "Visible"]);

export type EventParticipationLevel = zod.input<typeof EventParticipationLevel>;
export type EventParticipationLevelOutput = zod.output<typeof EventParticipationLevel>;

export const EventShare = /*#__PURE__*/ zod.strictObject({
  account_id: /*#__PURE__*/ zod.string(),
  discoverable_distance: DegreeOfSeparation,
  friend_group_id: /*#__PURE__*/ zod.int(),
});

export type EventShare = zod.input<typeof EventShare>;
export type EventShareOutput = zod.output<typeof EventShare>;

export const EventStatus = /*#__PURE__*/ zod.enum(["NotReady", "Complete", "Current", "Upcoming"]);

export type EventStatus = zod.input<typeof EventStatus>;
export type EventStatusOutput = zod.output<typeof EventStatus>;

export const EventVisibilityReason = /*#__PURE__*/ zod.enum([
  "Hosting",
  "Invited",
  "Shared",
  "Discovered",
  "Attending",
]);

export type EventVisibilityReason = zod.input<typeof EventVisibilityReason>;
export type EventVisibilityReasonOutput = zod.output<typeof EventVisibilityReason>;

export const EventWidgetLayout = /*#__PURE__*/ zod.strictObject({
  column: /*#__PURE__*/ zod.int(),
  id: /*#__PURE__*/ zod.uuid(),
  row: /*#__PURE__*/ zod.int(),
});

export type EventWidgetLayout = zod.input<typeof EventWidgetLayout>;
export type EventWidgetLayoutOutput = zod.output<typeof EventWidgetLayout>;

export const FieldError = /*#__PURE__*/ zod.strictObject({
  code: /*#__PURE__*/ zod.string(),
  detail: /*#__PURE__*/ zod.unknown(),
  message: /*#__PURE__*/ zod.string(),
  path: /*#__PURE__*/ zod
    .array(/*#__PURE__*/ zod.string())
    .check(
      /*#__PURE__*/ zod.describe(
        "The path within the request body to the field in which the error is detected.",
      ),
    ),
});

export type FieldError = zod.input<typeof FieldError>;
export type FieldErrorOutput = zod.output<typeof FieldError>;

export const Friend = /*#__PURE__*/ zod.strictObject({
  created_at: /*#__PURE__*/ zod.date(),
  note: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  profile: Profile,
  verified_at: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.date()),
});

export type Friend = zod.input<typeof Friend>;
export type FriendOutput = zod.output<typeof Friend>;

export const FriendRequest = /*#__PURE__*/ zod.strictObject({
  created_at: /*#__PURE__*/ zod.date(),
  note: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.string()),
  profile: Profile,
});

export type FriendRequest = zod.input<typeof FriendRequest>;
export type FriendRequestOutput = zod.output<typeof FriendRequest>;

export const GetAccountResponse = /*#__PURE__*/ zod.strictObject({
  profile: Profile,
});

export type GetAccountResponse = zod.input<typeof GetAccountResponse>;
export type GetAccountResponseOutput = zod.output<typeof GetAccountResponse>;

export const GetEmailVerificationRequestResponse = /*#__PURE__*/ zod.strictObject({
  email_verification: EmailVerificationRequest,
});

export type GetEmailVerificationRequestResponse = zod.input<
  typeof GetEmailVerificationRequestResponse
>;
export type GetEmailVerificationRequestResponseOutput = zod.output<
  typeof GetEmailVerificationRequestResponse
>;

export const GetEventResponse = /*#__PURE__*/ zod.strictObject({
  event: Event,
  event_participants: /*#__PURE__*/ zod.array(EventParticipant),
  event_widgets: /*#__PURE__*/ zod.array(EventWidget),
});

export type GetEventResponse = zod.input<typeof GetEventResponse>;
export type GetEventResponseOutput = zod.output<typeof GetEventResponse>;

export const GetFriendGroupResponse = /*#__PURE__*/ zod.strictObject({
  friend_group: FriendGroup,
  members: /*#__PURE__*/ zod.array(Friend),
});

export type GetFriendGroupResponse = zod.input<typeof GetFriendGroupResponse>;
export type GetFriendGroupResponseOutput = zod.output<typeof GetFriendGroupResponse>;

export const ListAccountEmailAddressesResponse = /*#__PURE__*/ zod.strictObject({
  email_addresses: /*#__PURE__*/ zod.array(EmailAddress),
});

export type ListAccountEmailAddressesResponse = zod.input<typeof ListAccountEmailAddressesResponse>;
export type ListAccountEmailAddressesResponseOutput = zod.output<
  typeof ListAccountEmailAddressesResponse
>;

export const listAccountEventsItemCurrentAttendeesMin = 0;

export const listAccountEventsItemPendingInvitesMin = 0;

export const ListAccountEventsItem = /*#__PURE__*/ zod.strictObject({
  current_attendees: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(listAccountEventsItemCurrentAttendeesMin))
    .check(/*#__PURE__*/ zod.describe("Total number of people who responded they are attending")),
  event: Event,
  pending_invites: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(listAccountEventsItemPendingInvitesMin))
    .check(
      /*#__PURE__*/ zod.describe(
        "Total number of people invited via invite or share who have not yet responded",
      ),
    ),
  response: /*#__PURE__*/ zod.exactOptional(
    /*#__PURE__*/ zod.union([
      /*#__PURE__*/ zod.null(),
      EventResponse.check(
        /*#__PURE__*/ zod.describe("The requesting user's current response to the event"),
      ),
    ]),
  ),
  visibility_reason: EventVisibilityReason.check(
    /*#__PURE__*/ zod.describe("Why the requesting user is able to see this event"),
  ),
});

export type ListAccountEventsItem = zod.input<typeof ListAccountEventsItem>;
export type ListAccountEventsItemOutput = zod.output<typeof ListAccountEventsItem>;

export const pageInfoTotalCountMin = 0;

export const PageInfo = /*#__PURE__*/ zod.strictObject({
  has_next_page: /*#__PURE__*/ zod
    .boolean()
    .check(/*#__PURE__*/ zod.describe("Whether there is another page to request or not.")),
  next_cursor: /*#__PURE__*/ zod
    .nullish(/*#__PURE__*/ zod.string())
    .check(
      /*#__PURE__*/ zod.describe(
        "Opaque cursor string, to be used in pagination APIs. Do not try to interpret the value, as the specifics are unstable.",
      ),
    ),
  total_count: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(pageInfoTotalCountMin))
    .check(/*#__PURE__*/ zod.describe("The total number of items being paginated through.")),
});

export type PageInfo = zod.input<typeof PageInfo>;
export type PageInfoOutput = zod.output<typeof PageInfo>;

export const ListAccountEventsResponse = /*#__PURE__*/ zod.strictObject({
  events: /*#__PURE__*/ zod.array(ListAccountEventsItem),
  page_info: PageInfo,
});

export type ListAccountEventsResponse = zod.input<typeof ListAccountEventsResponse>;
export type ListAccountEventsResponseOutput = zod.output<typeof ListAccountEventsResponse>;

export const ListCalendarsResponse = /*#__PURE__*/ zod.strictObject({
  calendars: /*#__PURE__*/ zod.array(Calendar),
});

export type ListCalendarsResponse = zod.input<typeof ListCalendarsResponse>;
export type ListCalendarsResponseOutput = zod.output<typeof ListCalendarsResponse>;

export const ListEventActionLogEntriesResponse = /*#__PURE__*/ zod.strictObject({
  event_action_log: /*#__PURE__*/ zod.array(EventActionLogEntry),
  page_info: PageInfo,
});

export type ListEventActionLogEntriesResponse = zod.input<typeof ListEventActionLogEntriesResponse>;
export type ListEventActionLogEntriesResponseOutput = zod.output<
  typeof ListEventActionLogEntriesResponse
>;

export const ListEventInviteCodesResponse = /*#__PURE__*/ zod.strictObject({
  event_invite_codes: /*#__PURE__*/ zod.array(EventInviteCode),
});

export type ListEventInviteCodesResponse = zod.input<typeof ListEventInviteCodesResponse>;
export type ListEventInviteCodesResponseOutput = zod.output<typeof ListEventInviteCodesResponse>;

export const ListEventInvitesResponse = /*#__PURE__*/ zod.strictObject({
  event_invites: /*#__PURE__*/ zod.array(EventInvite),
});

export type ListEventInvitesResponse = zod.input<typeof ListEventInvitesResponse>;
export type ListEventInvitesResponseOutput = zod.output<typeof ListEventInvitesResponse>;

export const ListEventSharesResponse = /*#__PURE__*/ zod.strictObject({
  event_shares: /*#__PURE__*/ zod.array(EventShare),
});

export type ListEventSharesResponse = zod.input<typeof ListEventSharesResponse>;
export type ListEventSharesResponseOutput = zod.output<typeof ListEventSharesResponse>;

export const listEventsItemCurrentAttendeesMin = 0;

export const listEventsItemPendingInvitesMin = 0;

export const ListEventsItem = /*#__PURE__*/ zod.strictObject({
  current_attendees: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(listEventsItemCurrentAttendeesMin)),
  event: Event,
  host: Profile,
  pending_invites: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(listEventsItemPendingInvitesMin)),
  response: /*#__PURE__*/ zod.exactOptional(
    /*#__PURE__*/ zod.union([
      /*#__PURE__*/ zod.null(),
      EventResponse.check(
        /*#__PURE__*/ zod.describe("The requesting user's current response to the event"),
      ),
    ]),
  ),
  visibility_reason: EventVisibilityReason,
});

export type ListEventsItem = zod.input<typeof ListEventsItem>;
export type ListEventsItemOutput = zod.output<typeof ListEventsItem>;

export const ListEventsResponse = /*#__PURE__*/ zod.strictObject({
  events: /*#__PURE__*/ zod.array(ListEventsItem),
  page_info: PageInfo,
});

export type ListEventsResponse = zod.input<typeof ListEventsResponse>;
export type ListEventsResponseOutput = zod.output<typeof ListEventsResponse>;

export const ListFriendGroupsResponse = /*#__PURE__*/ zod.strictObject({
  friend_groups: /*#__PURE__*/ zod.array(FriendGroup),
});

export type ListFriendGroupsResponse = zod.input<typeof ListFriendGroupsResponse>;
export type ListFriendGroupsResponseOutput = zod.output<typeof ListFriendGroupsResponse>;

export const ListFriendRequestsResponse = /*#__PURE__*/ zod.strictObject({
  received_friend_requests: /*#__PURE__*/ zod.array(FriendRequest),
  sent_friend_requests: /*#__PURE__*/ zod.array(FriendRequest),
});

export type ListFriendRequestsResponse = zod.input<typeof ListFriendRequestsResponse>;
export type ListFriendRequestsResponseOutput = zod.output<typeof ListFriendRequestsResponse>;

export const ListFriendsResponse = /*#__PURE__*/ zod.strictObject({
  friends: /*#__PURE__*/ zod.array(Friend),
});

export type ListFriendsResponse = zod.input<typeof ListFriendsResponse>;
export type ListFriendsResponseOutput = zod.output<typeof ListFriendsResponse>;

export const PushNotification = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    event_id: /*#__PURE__*/ zod.int(),
    type: /*#__PURE__*/ zod.enum(["EventInviteReceived"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    type: /*#__PURE__*/ zod.enum(["FriendRequestReceived"]),
  }),
  /*#__PURE__*/ zod.strictObject({
    account_id: /*#__PURE__*/ zod.string(),
    type: /*#__PURE__*/ zod.enum(["FriendRequestAccepted"]),
  }),
]);

export type PushNotification = zod.input<typeof PushNotification>;
export type PushNotificationOutput = zod.output<typeof PushNotification>;

export const Notification = /*#__PURE__*/ zod.intersection(
  PushNotification,
  /*#__PURE__*/ zod.strictObject({
    acknowledged_at: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.date()),
    created_at: /*#__PURE__*/ zod.date(),
    id: /*#__PURE__*/ zod.int(),
  }),
);

export type Notification = zod.input<typeof Notification>;
export type NotificationOutput = zod.output<typeof Notification>;

export const ListNotificationsResponse = /*#__PURE__*/ zod.strictObject({
  notifications: /*#__PURE__*/ zod.array(Notification),
  page_info: PageInfo,
});

export type ListNotificationsResponse = zod.input<typeof ListNotificationsResponse>;
export type ListNotificationsResponseOutput = zod.output<typeof ListNotificationsResponse>;

export const Session = /*#__PURE__*/ zod.strictObject({
  created_at: /*#__PURE__*/ zod.date(),
  expires_at: /*#__PURE__*/ zod.date(),
  id: /*#__PURE__*/ zod.uuid(),
});

export type Session = zod.input<typeof Session>;
export type SessionOutput = zod.output<typeof Session>;

export const ListSessionsResponse = /*#__PURE__*/ zod.strictObject({
  current_session_id: /*#__PURE__*/ zod.uuid(),
  sessions: /*#__PURE__*/ zod.array(Session),
});

export type ListSessionsResponse = zod.input<typeof ListSessionsResponse>;
export type ListSessionsResponseOutput = zod.output<typeof ListSessionsResponse>;

export const LoginRequest = /*#__PURE__*/ zod.strictObject({
  id: /*#__PURE__*/ zod.string(),
  password: /*#__PURE__*/ zod.string(),
  remember_me: /*#__PURE__*/ zod.boolean(),
});

export type LoginRequest = zod.input<typeof LoginRequest>;
export type LoginRequestOutput = zod.output<typeof LoginRequest>;

export const NotificationStatus = /*#__PURE__*/ zod.enum(["Unread", "Read"]);

export type NotificationStatus = zod.input<typeof NotificationStatus>;
export type NotificationStatusOutput = zod.output<typeof NotificationStatus>;

export const PushNotificationPayload = /*#__PURE__*/ zod.strictObject({
  data: PushNotification,
  id: /*#__PURE__*/ zod.int(),
});

export type PushNotificationPayload = zod.input<typeof PushNotificationPayload>;
export type PushNotificationPayloadOutput = zod.output<typeof PushNotificationPayload>;

export const RemoveFriendGroupMemberRequest = /*#__PURE__*/ zod.strictObject({
  friend_id: /*#__PURE__*/ zod.string(),
});

export type RemoveFriendGroupMemberRequest = zod.input<typeof RemoveFriendGroupMemberRequest>;
export type RemoveFriendGroupMemberRequestOutput = zod.output<
  typeof RemoveFriendGroupMemberRequest
>;

export const resetPasswordRequestPasswordMin = 12;

export const ResetPasswordRequest = /*#__PURE__*/ zod.strictObject({
  password: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(resetPasswordRequestPasswordMin)),
});

export type ResetPasswordRequest = zod.input<typeof ResetPasswordRequest>;
export type ResetPasswordRequestOutput = zod.output<typeof ResetPasswordRequest>;

export const SearchProfilesResponse = /*#__PURE__*/ zod.strictObject({
  profiles: /*#__PURE__*/ zod.array(Profile),
});

export type SearchProfilesResponse = zod.input<typeof SearchProfilesResponse>;
export type SearchProfilesResponseOutput = zod.output<typeof SearchProfilesResponse>;

export const SubscribeNotificationsRequest = /*#__PURE__*/ zod.strictObject({
  auth: /*#__PURE__*/ zod.string(),
  encodings: /*#__PURE__*/ zod.array(/*#__PURE__*/ zod.string()),
  endpoint: /*#__PURE__*/ zod.string(),
  p256dh: /*#__PURE__*/ zod.string(),
  platform: /*#__PURE__*/ zod.enum(["Web"]),
});

export type SubscribeNotificationsRequest = zod.input<typeof SubscribeNotificationsRequest>;
export type SubscribeNotificationsRequestOutput = zod.output<typeof SubscribeNotificationsRequest>;

export const updateAccountRequestBiographyMax = 65536;

export const updateAccountRequestDisplayNameMax = 512;

export const updateAccountRequestPronounsMax = 512;

export const updateAccountRequestRealNameMax = 512;

export const UpdateAccountRequest = /*#__PURE__*/ zod.strictObject({
  avatar_image_id: /*#__PURE__*/ zod
    .nullable(/*#__PURE__*/ zod.int())
    .check(
      /*#__PURE__*/ zod.describe(
        "Get an avatar image ID from [`upload_avatar`](#tag/accounts/POST/api/v1/avatars)",
      ),
    ),
  biography: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateAccountRequestBiographyMax)),
  display_name: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod
      .string()
      .check(/*#__PURE__*/ zod.maxLength(updateAccountRequestDisplayNameMax)),
  ),
  pronouns: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.maxLength(updateAccountRequestPronounsMax)),
  ),
  real_name: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.maxLength(updateAccountRequestRealNameMax)),
  ),
});

export type UpdateAccountRequest = zod.input<typeof UpdateAccountRequest>;
export type UpdateAccountRequestOutput = zod.output<typeof UpdateAccountRequest>;

export const UpdateAccountResponse = /*#__PURE__*/ zod.strictObject({
  profile: Profile,
});

export type UpdateAccountResponse = zod.input<typeof UpdateAccountResponse>;
export type UpdateAccountResponseOutput = zod.output<typeof UpdateAccountResponse>;

export const UpdateEmailAddressRequest = /*#__PURE__*/ zod.strictObject({
  is_primary: /*#__PURE__*/ zod
    .boolean()
    .check(
      /*#__PURE__*/ zod.describe(
        "One email address must be primary at any time; setting `is_primary` on the current primary email\naddress to false is invalid.",
      ),
    ),
});

export type UpdateEmailAddressRequest = zod.input<typeof UpdateEmailAddressRequest>;
export type UpdateEmailAddressRequestOutput = zod.output<typeof UpdateEmailAddressRequest>;

export const UpdateEmailAddressResponse = /*#__PURE__*/ zod.strictObject({
  email_address: EmailAddress,
});

export type UpdateEmailAddressResponse = zod.input<typeof UpdateEmailAddressResponse>;
export type UpdateEmailAddressResponseOutput = zod.output<typeof UpdateEmailAddressResponse>;

export const updateEventDirectionsRequestDirectionsMax = 65536;

export const updateEventDirectionsRequestPlaceIdMax = 256;

export const UpdateEventDirectionsRequest = /*#__PURE__*/ zod.strictObject({
  directions: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventDirectionsRequestDirectionsMax)),
  place_id: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventDirectionsRequestPlaceIdMax)),
});

export type UpdateEventDirectionsRequest = zod.input<typeof UpdateEventDirectionsRequest>;
export type UpdateEventDirectionsRequestOutput = zod.output<typeof UpdateEventDirectionsRequest>;

export const UpdateEventDirectionsResponse = /*#__PURE__*/ zod.strictObject({
  event_directions: EventDirections,
});

export type UpdateEventDirectionsResponse = zod.input<typeof UpdateEventDirectionsResponse>;
export type UpdateEventDirectionsResponseOutput = zod.output<typeof UpdateEventDirectionsResponse>;

export const UpdateEventInviteRequest = /*#__PURE__*/ zod.strictObject({
  discoverable_distance: DegreeOfSeparation.check(
    /*#__PURE__*/ zod.describe(
      "Sets the maximum discoverability distance for further participants chained from this one.\nSet to 0, friends of this participant will not be able to see this event without another\nsource of visibility.\n\nIf a more strict discoverable distance is set on the path by which this account is a\nparticipant, that distance effectively overrides this one.",
    ),
  ),
});

export type UpdateEventInviteRequest = zod.input<typeof UpdateEventInviteRequest>;
export type UpdateEventInviteRequestOutput = zod.output<typeof UpdateEventInviteRequest>;

export const UpdateEventInviteResponse = /*#__PURE__*/ zod.strictObject({
  event_invite: EventInvite,
});

export type UpdateEventInviteResponse = zod.input<typeof UpdateEventInviteResponse>;
export type UpdateEventInviteResponseOutput = zod.output<typeof UpdateEventInviteResponse>;

export const updateEventNoteRequestContentMax = 65536;

export const updateEventNoteRequestTitleMax = 256;

export const UpdateEventNoteRequest = /*#__PURE__*/ zod.strictObject({
  content: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventNoteRequestContentMax)),
  title: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventNoteRequestTitleMax)),
});

export type UpdateEventNoteRequest = zod.input<typeof UpdateEventNoteRequest>;
export type UpdateEventNoteRequestOutput = zod.output<typeof UpdateEventNoteRequest>;

export const UpdateEventNoteResponse = /*#__PURE__*/ zod.strictObject({
  event_note: EventNote,
});

export type UpdateEventNoteResponse = zod.input<typeof UpdateEventNoteResponse>;
export type UpdateEventNoteResponseOutput = zod.output<typeof UpdateEventNoteResponse>;

export const UpdateEventParticipationRequest = /*#__PURE__*/ zod.union([
  /*#__PURE__*/ zod
    .strictObject({
      discoverable_distance: DegreeOfSeparation.check(
        /*#__PURE__*/ zod.describe(
          "Sets the maximum discoverability distance for further participants chained from this one.\nSet to 0, friends of this participant will not be able to see this event without another\nsource of visibility.\n\nIf a more strict discoverable distance is set on the path by which this account is a\nparticipant, that distance effectively overrides this one.",
        ),
      ),
      response: /*#__PURE__*/ zod.enum(["Attending"]),
    })
    .check(/*#__PURE__*/ zod.describe("Respond that you will attend, and set relevant details.")),
  /*#__PURE__*/ zod
    .strictObject({
      response: /*#__PURE__*/ zod.enum(["NotAttending"]),
    })
    .check(
      /*#__PURE__*/ zod.describe(
        "Respond that you will not attend, and hide this event from the list in future.",
      ),
    ),
  /*#__PURE__*/ zod
    .strictObject({
      response: /*#__PURE__*/ zod.enum(["Ignore"]),
    })
    .check(
      /*#__PURE__*/ zod.describe(
        "Hide this event from your list, but without explicitly responding.",
      ),
    ),
]);

export type UpdateEventParticipationRequest = zod.input<typeof UpdateEventParticipationRequest>;
export type UpdateEventParticipationRequestOutput = zod.output<
  typeof UpdateEventParticipationRequest
>;

export const UpdateEventParticipationResponse = /*#__PURE__*/ zod.strictObject({
  event_participant: EventParticipant,
});

export type UpdateEventParticipationResponse = zod.input<typeof UpdateEventParticipationResponse>;
export type UpdateEventParticipationResponseOutput = zod.output<
  typeof UpdateEventParticipationResponse
>;

export const updateEventPollOptionTitleMax = 500;

export const UpdateEventPollOption = /*#__PURE__*/ zod.strictObject({
  id: /*#__PURE__*/ zod.nullish(/*#__PURE__*/ zod.int()),
  title: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(updateEventPollOptionTitleMax)),
});

export type UpdateEventPollOption = zod.input<typeof UpdateEventPollOption>;
export type UpdateEventPollOptionOutput = zod.output<typeof UpdateEventPollOption>;

export const updateEventPollRequestSubjectMax = 65536;

export const UpdateEventPollRequest = /*#__PURE__*/ zod.strictObject({
  allow_custom_options: /*#__PURE__*/ zod.boolean(),
  is_anonymous: /*#__PURE__*/ zod.boolean(),
  is_response_editable: /*#__PURE__*/ zod.boolean(),
  is_result_visible_before_voting: /*#__PURE__*/ zod.boolean(),
  max_selections: /*#__PURE__*/ zod.nullish(
    /*#__PURE__*/ zod.int().check(/*#__PURE__*/ zod.gte(1)),
  ),
  options: /*#__PURE__*/ zod.array(UpdateEventPollOption),
  subject: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventPollRequestSubjectMax)),
});

export type UpdateEventPollRequest = zod.input<typeof UpdateEventPollRequest>;
export type UpdateEventPollRequestOutput = zod.output<typeof UpdateEventPollRequest>;

export const UpdateEventPollResponse = /*#__PURE__*/ zod.strictObject({
  event_poll: EventPoll,
});

export type UpdateEventPollResponse = zod.input<typeof UpdateEventPollResponse>;
export type UpdateEventPollResponseOutput = zod.output<typeof UpdateEventPollResponse>;

export const UpdateEventPollVotesRequest = /*#__PURE__*/ zod.strictObject({
  votes: /*#__PURE__*/ zod.array(/*#__PURE__*/ zod.int()),
});

export type UpdateEventPollVotesRequest = zod.input<typeof UpdateEventPollVotesRequest>;
export type UpdateEventPollVotesRequestOutput = zod.output<typeof UpdateEventPollVotesRequest>;

export const UpdateEventPollVotesResponse = /*#__PURE__*/ zod.strictObject({
  event_poll: EventPoll,
});

export type UpdateEventPollVotesResponse = zod.input<typeof UpdateEventPollVotesResponse>;
export type UpdateEventPollVotesResponseOutput = zod.output<typeof UpdateEventPollVotesResponse>;

export const updateEventRequestDescriptionMax = 65536;

export const updateEventRequestLocationMax = 512;

export const updateEventRequestMaxAttendeesMin = 2;

export const updateEventRequestNameMax = 256;

export const UpdateEventRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.maxLength(updateEventRequestDescriptionMax))
    .check(/*#__PURE__*/ zod.describe("Details shown to anyone who can see this event.")),
  end_time: /*#__PURE__*/ zod.nullable(/*#__PURE__*/ zod.date()),
  location: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.maxLength(updateEventRequestLocationMax)),
  ),
  max_attendees: /*#__PURE__*/ zod
    .nullable(
      /*#__PURE__*/ zod.int().check(/*#__PURE__*/ zod.gte(updateEventRequestMaxAttendeesMin)),
    )
    .check(
      /*#__PURE__*/ zod.describe(
        "Set a limit on how many people may join this event. Leave null for unlimited.",
      ),
    ),
  name: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(updateEventRequestNameMax)),
  start_time: /*#__PURE__*/ zod.nullable(/*#__PURE__*/ zod.date()),
});

export type UpdateEventRequest = zod.input<typeof UpdateEventRequest>;
export type UpdateEventRequestOutput = zod.output<typeof UpdateEventRequest>;

export const UpdateEventResponse = /*#__PURE__*/ zod.strictObject({
  event: Event,
});

export type UpdateEventResponse = zod.input<typeof UpdateEventResponse>;
export type UpdateEventResponseOutput = zod.output<typeof UpdateEventResponse>;

export const UpdateEventShareRequest = /*#__PURE__*/ zod.strictObject({
  discoverable_distance: DegreeOfSeparation.check(
    /*#__PURE__*/ zod.describe(
      "Sets the maximum discoverability distance for further participants chained from this one.\nSet to 0, friends of this participant will not be able to see this event without another\nsource of visibility.\n\nIf a more strict discoverable distance is set on the path by which this account is a\nparticipant, that distance effectively overrides this one.",
    ),
  ),
});

export type UpdateEventShareRequest = zod.input<typeof UpdateEventShareRequest>;
export type UpdateEventShareRequestOutput = zod.output<typeof UpdateEventShareRequest>;

export const UpdateEventShareResponse = /*#__PURE__*/ zod.strictObject({
  event_share: EventShare,
});

export type UpdateEventShareResponse = zod.input<typeof UpdateEventShareResponse>;
export type UpdateEventShareResponseOutput = zod.output<typeof UpdateEventShareResponse>;

export const UpdateEventWidgetLayoutRequest = /*#__PURE__*/ zod.strictObject({
  widget_layout: /*#__PURE__*/ zod.array(EventWidgetLayout),
});

export type UpdateEventWidgetLayoutRequest = zod.input<typeof UpdateEventWidgetLayoutRequest>;
export type UpdateEventWidgetLayoutRequestOutput = zod.output<
  typeof UpdateEventWidgetLayoutRequest
>;

export const UpdateEventWidgetLayoutResponse = /*#__PURE__*/ zod.strictObject({
  widget_layout: /*#__PURE__*/ zod.array(EventWidgetLayout),
});

export type UpdateEventWidgetLayoutResponse = zod.input<typeof UpdateEventWidgetLayoutResponse>;
export type UpdateEventWidgetLayoutResponseOutput = zod.output<
  typeof UpdateEventWidgetLayoutResponse
>;

export const updateFriendGroupRequestDescriptionMax = 512;

export const updateFriendGroupRequestNameMax = 64;

export const UpdateFriendGroupRequest = /*#__PURE__*/ zod.strictObject({
  description: /*#__PURE__*/ zod.nullish(
    /*#__PURE__*/ zod
      .string()
      .check(/*#__PURE__*/ zod.maxLength(updateFriendGroupRequestDescriptionMax)),
  ),
  name: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(1))
    .check(/*#__PURE__*/ zod.maxLength(updateFriendGroupRequestNameMax)),
  sort: /*#__PURE__*/ zod
    .int()
    .check(
      /*#__PURE__*/ zod.describe(
        "The new sort value for this friend group.\n\nOther groups with sort value between the new and old values of the updated\nfriend group will be shifted towards the old value.",
      ),
    ),
});

export type UpdateFriendGroupRequest = zod.input<typeof UpdateFriendGroupRequest>;
export type UpdateFriendGroupRequestOutput = zod.output<typeof UpdateFriendGroupRequest>;

export const UpdateFriendGroupResponse = /*#__PURE__*/ zod.strictObject({
  friend_group: FriendGroup,
});

export type UpdateFriendGroupResponse = zod.input<typeof UpdateFriendGroupResponse>;
export type UpdateFriendGroupResponseOutput = zod.output<typeof UpdateFriendGroupResponse>;

export const updateFriendRequestNoteMax = 512;

export const UpdateFriendRequest = /*#__PURE__*/ zod.strictObject({
  note: /*#__PURE__*/ zod.nullable(
    /*#__PURE__*/ zod.string().check(/*#__PURE__*/ zod.maxLength(updateFriendRequestNoteMax)),
  ),
});

export type UpdateFriendRequest = zod.input<typeof UpdateFriendRequest>;
export type UpdateFriendRequestOutput = zod.output<typeof UpdateFriendRequest>;

export const UpdateFriendResponse = /*#__PURE__*/ zod.strictObject({
  friend: Friend,
});

export type UpdateFriendResponse = zod.input<typeof UpdateFriendResponse>;
export type UpdateFriendResponseOutput = zod.output<typeof UpdateFriendResponse>;

export const UpdateNotificationRequest = /*#__PURE__*/ zod.strictObject({
  is_acknowledged: /*#__PURE__*/ zod.boolean(),
});

export type UpdateNotificationRequest = zod.input<typeof UpdateNotificationRequest>;
export type UpdateNotificationRequestOutput = zod.output<typeof UpdateNotificationRequest>;

export const UpdateNotificationResponse = /*#__PURE__*/ zod.strictObject({
  notification: Notification,
});

export type UpdateNotificationResponse = zod.input<typeof UpdateNotificationResponse>;
export type UpdateNotificationResponseOutput = zod.output<typeof UpdateNotificationResponse>;

export const updatePasswordRequestNewPasswordMin = 12;

export const UpdatePasswordRequest = /*#__PURE__*/ zod.strictObject({
  new_password: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(updatePasswordRequestNewPasswordMin))
    .check(/*#__PURE__*/ zod.describe("The account's new password.")),
  password: /*#__PURE__*/ zod
    .string()
    .check(
      /*#__PURE__*/ zod.describe(
        "The account's current password. This must be correct for the change password request to be accepted.",
      ),
    ),
});

export type UpdatePasswordRequest = zod.input<typeof UpdatePasswordRequest>;
export type UpdatePasswordRequestOutput = zod.output<typeof UpdatePasswordRequest>;

export const uploadAvatarRequestContentLengthMin = 0;

export const uploadAvatarRequestContentMd5Min = 32;
export const uploadAvatarRequestContentMd5Max = 32;

export const UploadAvatarRequest = /*#__PURE__*/ zod.strictObject({
  content_length: /*#__PURE__*/ zod
    .int()
    .check(/*#__PURE__*/ zod.gte(uploadAvatarRequestContentLengthMin))
    .check(/*#__PURE__*/ zod.describe("Image size in bytes.")),
  content_md5: /*#__PURE__*/ zod
    .string()
    .check(/*#__PURE__*/ zod.minLength(uploadAvatarRequestContentMd5Min))
    .check(/*#__PURE__*/ zod.maxLength(uploadAvatarRequestContentMd5Max))
    .check(/*#__PURE__*/ zod.describe("Hex-encoded MD5 hash of the image content.")),
  content_type: AvatarMimeType.check(/*#__PURE__*/ zod.describe("Mime type of image.")),
  file_name: /*#__PURE__*/ zod.string(),
});

export type UploadAvatarRequest = zod.input<typeof UploadAvatarRequest>;
export type UploadAvatarRequestOutput = zod.output<typeof UploadAvatarRequest>;

export const UploadAvatarResponse = /*#__PURE__*/ zod.strictObject({
  image_id: /*#__PURE__*/ zod
    .int()
    .check(
      /*#__PURE__*/ zod.describe(
        "Image ID to refer to this image in future requests (e.g. to set the avatar to the new image once uploaded).",
      ),
    ),
  upload_url: /*#__PURE__*/ zod
    .url()
    .check(/*#__PURE__*/ zod.describe("Upload the image using `PUT` to this URL.")),
});

export type UploadAvatarResponse = zod.input<typeof UploadAvatarResponse>;
export type UploadAvatarResponseOutput = zod.output<typeof UploadAvatarResponse>;

export const ValidationError = /*#__PURE__*/ zod.strictObject({
  field_errors: /*#__PURE__*/ zod.array(FieldError),
});

export type ValidationError = zod.input<typeof ValidationError>;
export type ValidationErrorOutput = zod.output<typeof ValidationError>;
export const listAccountEventsParamsLimitDefault = 25;
export const listAccountEventsParamsLimitMax = 100;

export const ListAccountEventsParams = /*#__PURE__*/ zod.strictObject({
  event_status: /*#__PURE__*/ zod.array(EventStatus),
  limit: /*#__PURE__*/ zod._default(
    /*#__PURE__*/ zod
      .int()
      .check(/*#__PURE__*/ zod.gte(1))
      .check(/*#__PURE__*/ zod.lte(listAccountEventsParamsLimitMax)),
    listAccountEventsParamsLimitDefault,
  ),
  after_cursor: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.string()),
});

export type ListAccountEventsParams = zod.input<typeof ListAccountEventsParams>;
export type ListAccountEventsParamsOutput = zod.output<typeof ListAccountEventsParams>;

export const ViewCalendarParams = /*#__PURE__*/ zod.strictObject({
  participation: EventParticipationLevel,
});

export type ViewCalendarParams = zod.input<typeof ViewCalendarParams>;
export type ViewCalendarParamsOutput = zod.output<typeof ViewCalendarParams>;

export const listEventsParamsLimitDefault = 25;
export const listEventsParamsLimitMax = 100;

export const ListEventsParams = /*#__PURE__*/ zod.strictObject({
  since_time: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.date()),
  limit: /*#__PURE__*/ zod._default(
    /*#__PURE__*/ zod
      .int()
      .check(/*#__PURE__*/ zod.gte(1))
      .check(/*#__PURE__*/ zod.lte(listEventsParamsLimitMax)),
    listEventsParamsLimitDefault,
  ),
  after_cursor: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.string()),
});

export type ListEventsParams = zod.input<typeof ListEventsParams>;
export type ListEventsParamsOutput = zod.output<typeof ListEventsParams>;

export const GetEventParams = /*#__PURE__*/ zod.strictObject({
  invite: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.string()),
});

export type GetEventParams = zod.input<typeof GetEventParams>;
export type GetEventParamsOutput = zod.output<typeof GetEventParams>;

export const listEventActionLogEntriesParamsLimitDefault = 25;
export const listEventActionLogEntriesParamsLimitMax = 100;

export const ListEventActionLogEntriesParams = /*#__PURE__*/ zod.strictObject({
  limit: /*#__PURE__*/ zod._default(
    /*#__PURE__*/ zod
      .int()
      .check(/*#__PURE__*/ zod.gte(1))
      .check(/*#__PURE__*/ zod.lte(listEventActionLogEntriesParamsLimitMax)),
    listEventActionLogEntriesParamsLimitDefault,
  ),
  after_cursor: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.string()),
});

export type ListEventActionLogEntriesParams = zod.input<typeof ListEventActionLogEntriesParams>;
export type ListEventActionLogEntriesParamsOutput = zod.output<
  typeof ListEventActionLogEntriesParams
>;

export const listNotificationsParamsLimitDefault = 25;
export const listNotificationsParamsLimitMax = 100;

export const ListNotificationsParams = /*#__PURE__*/ zod.strictObject({
  status: /*#__PURE__*/ zod.array(NotificationStatus),
  limit: /*#__PURE__*/ zod._default(
    /*#__PURE__*/ zod
      .int()
      .check(/*#__PURE__*/ zod.gte(1))
      .check(/*#__PURE__*/ zod.lte(listNotificationsParamsLimitMax)),
    listNotificationsParamsLimitDefault,
  ),
  after_cursor: /*#__PURE__*/ zod.exactOptional(/*#__PURE__*/ zod.string()),
});

export type ListNotificationsParams = zod.input<typeof ListNotificationsParams>;
export type ListNotificationsParamsOutput = zod.output<typeof ListNotificationsParams>;

export const SearchProfilesParams = /*#__PURE__*/ zod.strictObject({
  query: /*#__PURE__*/ zod.string(),
});

export type SearchProfilesParams = zod.input<typeof SearchProfilesParams>;
export type SearchProfilesParamsOutput = zod.output<typeof SearchProfilesParams>;
